import { forwardRef, MouseEventHandler, ReactElement } from 'react';
import { Box, Stack, Button, Typography, ButtonProps } from '@o2/ui';
import MuiDialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

export interface ConfirmModalProps {
  children?: ReactElement | ReactElement[] | string;
  open: boolean;
  onConfirm: MouseEventHandler;
  onCancel: MouseEventHandler;
  title?: string | ReactElement;
  description?: string | ReactElement;
  confirmBtnText?: string | ReactElement;
  cancelBtnText?: string | ReactElement;
  componentProps?: {
    // OPTIMIZE: FE-77
    confirmBtn?: ButtonProps & { 'data-elm'?: string };
    cancelBtn?: ButtonProps & { 'data-elm'?: string };
  };
}

const ModalBox = styled(Stack, {
  name: 'ModalBox',
  slot: 'root',
})(({ theme }) => ({
  backgroundColor: 'white',
  maxWidth: '100%',
  padding: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const ConfirmModal = forwardRef<HTMLDivElement, ConfirmModalProps>(
  (
    {
      open,
      onCancel,
      onConfirm,
      title,
      description,
      confirmBtnText = 'Pokračovat',
      cancelBtnText = 'Zrušit',
      children,
      componentProps,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const smallDevice = useMediaQuery(theme.breakpoints.down('md'));
    const content = (
      <>
        <Typography variant={smallDevice ? 'h5' : 'h6'} mb={1}>
          {title}
        </Typography>
        <Typography variant="body1">{description}</Typography>
      </>
    );

    return (
      <MuiDialog ref={ref} onClose={onCancel} open={open} {...props}>
        <ModalBox>
          <Box>{children ?? content}</Box>
          <Stack mt={3} direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="space-between">
            <Button variant="contained" onClick={onConfirm} {...componentProps?.confirmBtn}>
              {confirmBtnText}
            </Button>
            <Button variant="outlined" onClick={onCancel} {...componentProps?.cancelBtn}>
              {cancelBtnText}
            </Button>
          </Stack>
        </ModalBox>
      </MuiDialog>
    );
  }
);

ConfirmModal.displayName = 'ConfirmModal';

export default ConfirmModal;
